import React from 'react';
import { useTranslation } from 'react-i18next';
import { Popup } from '@/components/UI/Popup/Popup';
import * as S from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal.styles';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { Button } from '@/components/UI/Button/Button';
import { PaymentStatusData } from '@/bridge/types';
import {
  getPaymentAmount,
  getPaymentEntries,
  getRowName,
  getSuccessModalMessage,
  getSuccessModalTitle,
} from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal.helpers';
import { PaymentService, PaymentStatus } from '@/api/types/payments.types';
import { useWebViewPaymentModalStore } from '@/store/useWebViewPaymentModalStore';

export const WebViewPaymentStatusModal = () => {
  const { t } = useTranslation();
  const [paymentData, isOpen, close] = useWebViewPaymentModalStore(state => [
    state.paymentData,
    state.isOpen,
    state.close,
  ]);

  const payment = paymentData || ({} as PaymentStatusData);
  const isSuccess = payment.status === PaymentStatus.Success;
  const serviceId = 'service_id' in payment ? payment.service_id : '';
  const isAddBalance = serviceId === PaymentService.Add_balance_custom;

  const title = isSuccess
    ? getSuccessModalTitle(t, serviceId)
    : t('webview.payment_receipt.error_title');

  const message = isSuccess
    ? getSuccessModalMessage(t, serviceId)
    : t('webview.payment_receipt.error_message');

  const amount = isSuccess && getPaymentAmount(payment);

  const rows = getPaymentEntries(t, payment).map(([name, value]) => [
    getRowName(t, name, isAddBalance),
    value,
  ]);

  return (
    <Popup
      isOpen={isOpen}
      size="intermediate"
      close={close}
      overlayOpacity={0.6}
    >
      <S.Container>
        <IconBox
          $filename={`${isSuccess ? 'check' : 'cross'}-circle-rounded-halo.svg`}
          $width={50}
          $height={50}
        />
        <S.Head>
          <S.Title $variant="4">{title}</S.Title>
          <S.Message>{message}</S.Message>
        </S.Head>
        {isSuccess && (
          <S.Body>
            {rows.map(([name, value]) => {
              return (
                <S.Row key={name}>
                  <S.Label>{name}</S.Label>
                  <S.Value>{value}</S.Value>
                </S.Row>
              );
            })}
          </S.Body>
        )}
        {'amount' in payment && (
          <S.Row>
            <S.Label>{t('webview.payment_receipt.amount')}</S.Label>
            <S.Value $variant="6">{amount}</S.Value>
          </S.Row>
        )}
        <S.Actions>
          <Button
            appearance={isSuccess ? 'secondaryLight' : 'secondary'}
            isFullWidth
            onClick={close}
          >
            {t(`common.${isSuccess ? 'got_it' : 'try_again'}`)}
          </Button>
        </S.Actions>
      </S.Container>
    </Popup>
  );
};
